import { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import useCanvasActivity from "@/hooks/useCanvasActivity";

const RED_CONTOUR = [
  [-1.1338, 1.2102], [0.8025, 1.2102], [1.0064, 0.8790],
  [0.4459, -0.0510], [-0.1401, -0.0510], [-0.7643, -1.0318],
  [-1.1975, -1.0191], [-0.3439, 0.3439], [0.2420, 0.3439],
  [0.5478, 0.8408], [-0.9045, 0.8408],
];

const WHITE_CONTOUR = [
  [0.7771, 0.2420], [1.6178, -1.0446], [1.1720, -1.0446],
  [0.8662, -0.5605], [0.2420, -0.5605], [0.1656, -0.4459],
  [0.0255, -0.2038], [0.6369, -0.2038], [0.5732, -0.0764],
];

function createShape(points) {
  const shape = new THREE.Shape();
  shape.moveTo(points[0][0], points[0][1]);
  points.slice(1).forEach(([x, y]) => shape.lineTo(x, y));
  shape.closePath();
  return shape;
}

function ExtrudedPiece({ points, color, sideColor }) {
  const geometry = useMemo(() => {
    const result = new THREE.ExtrudeGeometry(createShape(points), {
      depth: 0.38,
      steps: 1,
      bevelEnabled: true,
      bevelSegments: 4,
      bevelSize: 0.026,
      bevelThickness: 0.026,
      curveSegments: 1,
    });
    result.computeVertexNormals();
    return result;
  }, [points]);

  const materials = useMemo(() => [
    new THREE.MeshPhysicalMaterial({
      color,
      metalness: 0.28,
      roughness: 0.22,
      clearcoat: 0.8,
      clearcoatRoughness: 0.18,
    }),
    new THREE.MeshStandardMaterial({
      color: sideColor,
      metalness: 0.38,
      roughness: 0.3,
    }),
  ], [color, sideColor]);

  useEffect(() => () => {
    geometry.dispose();
    materials.forEach((material) => material.dispose());
  }, [geometry, materials]);

  return <mesh geometry={geometry} material={materials} castShadow receiveShadow />;
}

function RaavaLogoMesh() {
  const group = useRef();
  const halos = useRef();
  const targetEuler = useMemo(() => new THREE.Euler(0, 0, 0, "YXZ"), []);
  const targetQuaternion = useMemo(() => new THREE.Quaternion(), []);
  const drag = useRef({
    active: false,
    lastX: 0,
    lastY: 0,
    yaw: 0,
    pitch: 0,
    velocityYaw: 0,
    velocityPitch: 0,
  });

  useFrame((state, delta) => {
    if (!group.current) return;
    const time = state.clock.elapsedTime;

    if (!drag.current.active) {
      drag.current.yaw += drag.current.velocityYaw;
      drag.current.pitch += drag.current.velocityPitch;
      drag.current.velocityYaw *= Math.pow(0.91, delta * 60);
      drag.current.velocityPitch *= Math.pow(0.91, delta * 60);
    }

    const targetY = drag.current.yaw + state.pointer.x * 0.48;
    const targetX = drag.current.pitch - state.pointer.y * 0.46;
    targetEuler.set(targetX, targetY, state.pointer.x * 0.025, "YXZ");
    targetQuaternion.setFromEuler(targetEuler);
    group.current.quaternion.slerp(targetQuaternion, 1 - Math.exp(-delta * 8));
    group.current.position.x = THREE.MathUtils.lerp(
      group.current.position.x,
      1.48 + state.pointer.x * 0.14,
      0.03,
    );
    group.current.position.y = THREE.MathUtils.lerp(
      group.current.position.y,
      state.pointer.y * 0.12,
      0.06,
    );
    group.current.position.z = THREE.MathUtils.lerp(
      group.current.position.z,
      (Math.abs(state.pointer.x) + Math.abs(state.pointer.y)) * 0.035,
      0.04,
    );
    group.current.scale.setScalar(1.18);

    if (halos.current) {
      halos.current.rotation.z += delta * 0.09;
      halos.current.rotation.y = Math.sin(time * 0.34) * 0.18;
    }
  });

  const beginDrag = (event) => {
    event.stopPropagation();
    event.nativeEvent?.preventDefault?.();
    drag.current.active = true;
    drag.current.lastX = event.clientX;
    drag.current.lastY = event.clientY;
    drag.current.velocityYaw = 0;
    drag.current.velocityPitch = 0;
    event.target.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event) => {
    if (!drag.current.active) return;
    event.stopPropagation();
    const deltaX = event.clientX - drag.current.lastX;
    const deltaY = event.clientY - drag.current.lastY;
    drag.current.yaw += deltaX * 0.009;
    drag.current.pitch += deltaY * 0.009;
    drag.current.velocityYaw = deltaX * 0.0018;
    drag.current.velocityPitch = deltaY * 0.0018;
    drag.current.lastX = event.clientX;
    drag.current.lastY = event.clientY;
  };

  const endDrag = (event) => {
    if (!drag.current.active) return;
    event.stopPropagation();
    drag.current.active = false;
    event.target.releasePointerCapture?.(event.pointerId);
  };

  return (
    <group
      ref={group}
      position={[1.48, 0, 0]}
      scale={1.18}
      onPointerDown={beginDrag}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerOut={(event) => {
        if (!drag.current.active) return;
        moveDrag(event);
      }}
    >
      <ExtrudedPiece points={RED_CONTOUR} color="#087CF4" sideColor="#032B78" />
      <ExtrudedPiece points={WHITE_CONTOUR} color="#ffffff" sideColor="#707078" />

      <group ref={halos}>
        <mesh position={[0.1, 0.02, -0.46]}>
          <circleGeometry args={[2.05, 64]} />
          <meshBasicMaterial
            color="#128CFF"
            transparent
            opacity={0.055}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
        <mesh rotation={[Math.PI / 2.5, 0.05, Math.PI / 5]} position={[0.1, 0, -0.58]}>
          <torusGeometry args={[2.18, 0.012, 6, 96]} />
          <meshBasicMaterial color="#33C4FF" transparent opacity={0.28} depthWrite={false} />
        </mesh>
        <mesh rotation={[Math.PI / 2.05, -0.25, -Math.PI / 4]} position={[0.1, 0, -0.66]}>
          <torusGeometry args={[2.52, 0.008, 6, 96]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.13} depthWrite={false} />
        </mesh>
      </group>
    </group>
  );
}

function DataField({ count = 320 }) {
  const ref = useRef();
  const positions = useMemo(() => {
    const array = new Float32Array(count * 3);
    for (let index = 0; index < count; index += 1) {
      const radius = 4.1 + Math.random() * 7.2;
      const theta = Math.random() * Math.PI * 2;
      array[index * 3] = Math.cos(theta) * radius;
      array[index * 3 + 1] = (Math.random() - 0.5) * 8;
      array[index * 3 + 2] = Math.sin(theta) * radius - 3;
    }
    return array;
  }, [count]);

  useFrame((state, delta) => {
    if (!ref.current) return;
    ref.current.rotation.y += delta * 0.01;
    ref.current.position.x = THREE.MathUtils.lerp(ref.current.position.x, state.pointer.x * -0.12, 0.02);
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.022} color="#3AA9FF" transparent opacity={0.36} sizeAttenuation depthWrite={false} />
    </points>
  );
}

export default function Hero3D() {
  const { hostRef, frameloop } = useCanvasActivity("160px");

  return (
    <div ref={hostRef} className="absolute inset-0 z-0" data-testid="hero-3d-canvas">
      <Canvas
        camera={{ position: [0, 0, 7], fov: 48 }}
        frameloop={frameloop}
        gl={{ alpha: true, antialias: true, powerPreference: "high-performance", stencil: false }}
        dpr={[1, 1.25]}
      >
        <ambientLight intensity={0.72} />
        <directionalLight position={[4, 5, 6]} intensity={2.8} color="#ffffff" />
        <pointLight position={[0.5, -1.6, 3.2]} intensity={28} distance={7} color="#0A7FFF" />
        <pointLight position={[4, 1.5, 1.5]} intensity={14} distance={6} color="#ffffff" />
        <RaavaLogoMesh />
        <DataField />
      </Canvas>
    </div>
  );
}
